import React from 'react';
import {
  TicketIcon,
  BoltIcon,
  InboxArrowDownIcon,
  BriefcaseIcon,
  ClockIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

export interface KpiCardItem {
  title: string;
  value: string;
  color?: string;
  /** When set, card opens KPI drill-down table for this metric key. */
  drillKey?: string;
}

export interface KpiCardsProps {
  items: KpiCardItem[];
  onDrillMetric?: (drillKey: string) => void;
}

const CARD_ACCENTS = [
  { border: 'border-l-indigo-500', ring: 'ring-indigo-500/10', iconWrap: 'bg-indigo-50 text-indigo-600', Icon: TicketIcon },
  { border: 'border-l-rose-500', ring: 'ring-rose-500/10', iconWrap: 'bg-rose-50 text-rose-600', Icon: BoltIcon },
  { border: 'border-l-amber-500', ring: 'ring-amber-500/10', iconWrap: 'bg-amber-50 text-amber-600', Icon: InboxArrowDownIcon },
  { border: 'border-l-sky-500', ring: 'ring-sky-500/10', iconWrap: 'bg-sky-50 text-sky-600', Icon: BriefcaseIcon },
  { border: 'border-l-violet-500', ring: 'ring-violet-500/10', iconWrap: 'bg-violet-50 text-violet-600', Icon: ClockIcon },
  { border: 'border-l-orange-500', ring: 'ring-orange-500/10', iconWrap: 'bg-orange-50 text-orange-600', Icon: ArrowPathIcon },
  { border: 'border-l-emerald-500', ring: 'ring-emerald-500/10', iconWrap: 'bg-emerald-50 text-emerald-600', Icon: CalendarDaysIcon },
] as const;

const KpiCards: React.FC<KpiCardsProps> = ({ items, onDrillMetric }) => (
  <div className="grid grid-cols-2 items-stretch gap-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-7">
    {items.map((kpi, i) => {
      const accent = CARD_ACCENTS[i % CARD_ACCENTS.length];
      const Icon = accent.Icon;
      const clickable = Boolean(kpi.drillKey && onDrillMetric);
      return (
        <div
          key={kpi.title}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? () => onDrillMetric!(kpi.drillKey!) : undefined}
          onKeyDown={
            clickable
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onDrillMetric!(kpi.drillKey!);
                  }
                }
              : undefined
          }
          className={`group relative flex h-full min-h-[5.5rem] flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-slate-900/10 ${accent.border} border-l-4 pl-3.5 sm:pl-4 ${accent.ring} ${clickable ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500' : ''}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${accent.iconWrap} transition-transform duration-200 group-hover:scale-105`}
            >
              <Icon className="h-5 w-5" aria-hidden />
            </div>
          </div>
          <div
            className={`mt-3 text-2xl font-bold tabular-nums tracking-tight ${kpi.color ?? 'text-slate-900'}`}
          >
            {kpi.value}
          </div>
          <div className="mt-1 line-clamp-2 text-xs font-medium leading-snug text-slate-500 sm:text-[13px]">
            {kpi.title}
          </div>
        </div>
      );
    })}
  </div>
);

export default KpiCards;
